import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem
} from '@mui/material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

const themeMarkdownSx = {
  color: 'text.primary',
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-child': { mb: 0 },
  '& h1': { typography: 'h5', fontWeight: 700, mt: 0, mb: 1.25, lineHeight: 1.3 },
  '& h2': { typography: 'h6', fontWeight: 700, mt: 2.25, mb: 1, lineHeight: 1.35 },
  '& h3': { typography: 'subtitle1', fontWeight: 700, mt: 1.75, mb: 0.75, lineHeight: 1.4 },
  '& h4, & h5, & h6': { typography: 'subtitle2', fontWeight: 700, mt: 1.5, mb: 0.5 },
  '& p': { typography: 'body2', mb: 1.1, lineHeight: 1.55 },
  '& ul, & ol': { pl: 2.75, mb: 1.25, mt: 0.25 },
  '& li': { typography: 'body2', mb: 0.4, lineHeight: 1.5 },
  '& li > p': { mb: 0.35 },
  '& strong': { fontWeight: 700 },
  '& em': { fontStyle: 'italic' },
  '& a': { color: 'primary.main' },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '0.85em',
    bgcolor: 'action.hover',
    px: 0.6,
    py: 0.15,
    borderRadius: 0.75
  },
  '& pre': {
    bgcolor: 'grey.50',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 1.5,
    overflowX: 'hidden',
    mb: 1.5,
    '& code': { bgcolor: 'transparent', p: 0 }
  },
  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'divider',
    pl: 1.5,
    ml: 0,
    my: 1.25,
    color: 'text.secondary'
  },
  '& hr': { my: 2, border: 0, borderTop: '1px solid', borderColor: 'divider' },
  '& table': { width: '100%', borderCollapse: 'collapse', mb: 1.5, typography: 'body2' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
  '& th': { bgcolor: 'action.hover', fontWeight: 700 }
} as const;

function themeStatusChipColor(status: string): 'default' | 'success' | 'warning' | 'info' {
  const s = (status || '').toLowerCase();
  if (s === 'published') return 'success';
  if (s === 'draft') return 'warning';
  if (s === 'archived') return 'default';
  return 'info';
}

type DimBlob = {
  label?: string;
  name?: string;
  confidence?: number;
  rationale?: string;
  sources?: string[];
  supplier_id?: number;
  category_id?: number;
  vat_rate?: string;
  vat_code_id?: number;
};

type PredictionRow = {
  id: number;
  tx_id: number;
  status: string;
  cashflow?: DimBlob;
  supplier?: DimBlob;
  category?: DimBlob;
  vat?: DimBlob;
  transaction?: {
    id: number;
    description?: string | null;
    amount?: number | null;
    currency?: string | null;
    started_date?: string | null;
    merchant_name?: string | null;
  };
};

type ThemeDoc = {
  id: number;
  theme_key: string;
  title: string;
  body_markdown: string;
  status: string;
  web_queries?: string[];
  web_snippets?: Array<{ title?: string; url?: string; snippet?: string }>;
};

function confChip(dim: DimBlob | undefined, fallbackLabel: string) {
  if (!dim || (!dim.label && !dim.name && dim.vat_rate == null && !dim.confidence)) {
    return <Chip size="small" label="—" variant="outlined" />;
  }
  const label = dim.label || dim.name || dim.vat_rate || fallbackLabel;
  const c = typeof dim.confidence === 'number' ? dim.confidence : 0;
  const color = c >= 0.75 ? 'success' : c >= 0.45 ? 'warning' : 'default';
  return (
    <Chip
      size="small"
      color={color as 'success' | 'warning' | 'default'}
      label={`${label} (${Math.round(c * 100)}%)`}
      title={dim.rationale || undefined}
    />
  );
}

type Props = {
  deepLinkTxId?: number | null;
  onOpenTx?: (txId: number) => void;
};

function _todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const SINCE_MAY_2026 = { from: '2026-05-01', to: _todayIso() };

export default function ProbabilisticTxTab({ deepLinkTxId, onOpenTx }: Props) {
  const { user } = useAuth();
  const isPrivileged = user?.role === 'admin' || user?.role === 'owner';

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [webEnabled, setWebEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [items, setItems] = useState<PredictionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(100);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(SINCE_MAY_2026.from);
  const [dateTo, setDateTo] = useState(SINCE_MAY_2026.to);
  const [selected, setSelected] = useState<number[]>([]);
  const [themes, setThemes] = useState<ThemeDoc[]>([]);
  const [activeThemeId, setActiveThemeId] = useState<number | null>(null);
  const [themeDraft, setThemeDraft] = useState('');
  const [themeTitle, setThemeTitle] = useState('');
  const [themeStatus, setThemeStatus] = useState('draft');
  const [themeBodyMode, setThemeBodyMode] = useState<'preview' | 'edit'>('preview');
  const [enableWebOnScore, setEnableWebOnScore] = useState(false);
  const [whyRow, setWhyRow] = useState<PredictionRow | null>(null);

  const activeTheme = useMemo(
    () => (activeThemeId == null ? null : themes.find((t) => t.id === activeThemeId) || null),
    [activeThemeId, themes]
  );

  const listParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: page + 1,
      per_page: perPage
    };
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (isPrivileged) params.all_users = true;
    return params;
  }, [page, perPage, statusFilter, dateFrom, dateTo, isPrivileged]);

  const loadStatus = useCallback(async () => {
    const res = await api.get('/api/probabilistic-tx/status');
    setEnabled(Boolean(res.data?.enabled));
    setWebEnabled(Boolean(res.data?.web_enabled));
  }, []);

  const loadPredictions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/probabilistic-tx/predictions', { params: listParams });
      setItems(res.data?.items || []);
      setTotal(Number(res.data?.total) || 0);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to load predictions';
      setError(String(msg));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listParams]);

  const loadThemes = useCallback(async () => {
    try {
      const params = isPrivileged ? { all_users: true } : {};
      const res = await api.get('/api/probabilistic-tx/theme-documents', { params });
      const list: ThemeDoc[] = res.data?.items || [];
      setThemes(list);
      setActiveThemeId((prev) => {
        if (prev != null && list.some((t) => t.id === prev)) return prev;
        if (list.length) {
          setThemeDraft(list[0].body_markdown || '');
          setThemeTitle(list[0].title || '');
          setThemeStatus(list[0].status || 'draft');
          return list[0].id;
        }
        return null;
      });
    } catch {
      /* tab may be disabled */
    }
  }, [isPrivileged]);

  useEffect(() => {
    loadStatus().catch(() => setEnabled(false));
  }, [loadStatus]);

  useEffect(() => {
    if (enabled) {
      loadPredictions();
      loadThemes();
    }
  }, [enabled, loadPredictions, loadThemes]);

  useEffect(() => {
    if (deepLinkTxId && items.length) {
      const row = items.find((r) => r.tx_id === deepLinkTxId);
      if (row) setWhyRow(row);
    }
  }, [deepLinkTxId, items]);

  const toggleSelect = (txId: number) => {
    setSelected((prev) => (prev.includes(txId) ? prev.filter((x) => x !== txId) : [...prev, txId]));
  };

  const scoreSelected = async () => {
    setMessage(null);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        enable_web: enableWebOnScore && webEnabled
      };
      if (selected.length) body.tx_ids = selected;
      else {
        body.all_uncommitted = true;
        body.missing_only = true;
      }
      const res = await api.post('/api/probabilistic-tx/score', body);
      setMessage(`Scored ${res.data?.scored ?? 0} of ${res.data?.requested ?? 0}`);
      await loadPredictions();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Score failed'));
    }
  };

  const acceptRow = async (txId: number, dims?: string[]) => {
    setError(null);
    try {
      const res = await api.post(`/api/probabilistic-tx/predictions/${txId}/accept`, {
        dims: dims || ['cashflow', 'supplier', 'category', 'vat']
      });
      const blocked = res.data?.blocked_user_specified || [];
      setMessage(
        blocked.length
          ? `Accepted ${txId}; blocked user_specified: ${blocked.join(', ')}`
          : `Accepted prediction for TX ${txId}`
      );
      await loadPredictions();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Accept failed'));
    }
  };

  const rejectRow = async (txId: number) => {
    try {
      await api.post(`/api/probabilistic-tx/predictions/${txId}/reject`);
      setMessage(`Rejected TX ${txId}`);
      await loadPredictions();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Reject failed'));
    }
  };

  const generateDrafts = async () => {
    setError(null);
    try {
      const res = await api.post('/api/probabilistic-tx/theme-documents/generate-drafts', {
        use_llm: true,
        limit_clusters: 30
      });
      setMessage(`Theme drafts: created ${res.data?.created ?? 0}, updated ${res.data?.updated ?? 0}`);
      await loadThemes();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Generate drafts failed'));
    }
  };

  const saveTheme = async () => {
    if (activeThemeId == null) return;
    try {
      await api.put(`/api/probabilistic-tx/theme-documents/${activeThemeId}`, {
        title: themeTitle,
        body_markdown: themeDraft,
        status: themeStatus,
        mark_human_corrected: true
      });
      setMessage('Theme document saved');
      await loadThemes();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Save theme failed'));
    }
  };

  const fetchWebForTheme = async () => {
    if (activeThemeId == null) return;
    try {
      await api.post(`/api/probabilistic-tx/theme-documents/${activeThemeId}/fetch-web`);
      setMessage('Web snippets fetched');
      await loadThemes();
    } catch (e: any) {
      setError(String(e?.response?.data?.error || e?.message || 'Web fetch failed'));
    }
  };

  const selectTheme = (id: number) => {
    const doc = themes.find((t) => t.id === id);
    setActiveThemeId(id);
    if (doc) {
      setThemeDraft(doc.body_markdown || '');
      setThemeTitle(doc.title || '');
      setThemeStatus(doc.status || 'draft');
    }
  };

  if (enabled === null) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!enabled) {
    return (
      <Alert severity="info">
        Probabilistic TX tags are disabled on this API host. Set <code>PROBABILISTIC_TX_ENABLED=1</code> (and
        optionally <code>LLM_OPENAI_API_KEY</code>) then redeploy/restart.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Parallel Cash flow / Supplier / Category / VAT predictions. Accept promotes into real tags; never
        auto-overwrites user-specified tags.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {message ? (
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Button variant="contained" size="small" onClick={scoreSelected} disabled={loading}>
          {selected.length ? `Score selected (${selected.length})` : 'Score uncommitted missing'}
        </Button>
        <Button variant="outlined" size="small" onClick={generateDrafts}>
          Generate theme drafts
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={enableWebOnScore}
              disabled={!webEnabled}
              onChange={(e) => setEnableWebOnScore(e.target.checked)}
            />
          }
          label="Enable web on score"
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setPage(0);
            setStatusFilter(e.target.value);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="scored">Scored</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="promoted">Promoted</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="stale">Stale</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="From"
          value={dateFrom}
          onChange={(e) => {
            setPage(0);
            setDateFrom(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={dateTo}
          onChange={(e) => {
            setPage(0);
            setDateTo(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setPage(0);
            setDateFrom(SINCE_MAY_2026.from);
            setDateTo(_todayIso());
          }}
        >
          Since May 2026
        </Button>
        <Button
          size="small"
          onClick={() => {
            setPage(0);
            setDateFrom('');
            setDateTo('');
          }}
        >
          Clear dates
        </Button>
        <Button size="small" onClick={() => loadPredictions()} disabled={loading}>
          Refresh
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total.toLocaleString()} prediction{total === 1 ? '' : 's'}
          {dateFrom || dateTo
            ? ` · ${dateFrom || '…'} → ${dateTo || '…'}`
            : ''}
        </Typography>
      </Stack>

      <Card variant="outlined">
        <CardContent sx={{ pt: 1.5 }}>
          <TableContainer sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: { xs: '55vh', md: 560 }, overflowY: 'auto' }}>
            <Table size="small" stickyHeader sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={equalFitCols(
                  ['sel', 'tx', 'date', 'description', 'amount', 'pcf', 'psup', 'pcat', 'pvat', 'status', 'actions'],
                  { flexKey: 'description' }
                )}
              />
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>TX</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>P-Cashflow</TableCell>
                  <TableCell>P-Supplier</TableCell>
                  <TableCell>P-Category</TableCell>
                  <TableCell>P-VAT</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Typography variant="body2" color="text.secondary">
                        No predictions in this filter. Try May–Jun 2026, clear status, or Score uncommitted
                        missing.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => (
                    <TableRow key={row.id} hover selected={whyRow?.id === row.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.includes(row.tx_id)}
                          onChange={() => toggleSelect(row.tx_id)}
                        />
                      </TableCell>
                      <TableCell>{row.tx_id}</TableCell>
                      <TableCell>
                        {(row.transaction?.started_date || '').slice(0, 10) || '—'}
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={fitTruncateSx}
                          title={row.transaction?.description || row.transaction?.merchant_name || ''}
                        >
                          {row.transaction?.description || row.transaction?.merchant_name || '—'}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {row.transaction?.amount != null
                          ? `${row.transaction.amount} ${row.transaction.currency || ''}`
                          : '—'}
                      </TableCell>
                      <TableCell>{confChip(row.cashflow, 'CF')}</TableCell>
                      <TableCell>{confChip(row.supplier, 'Sup')}</TableCell>
                      <TableCell>{confChip(row.category, 'Cat')}</TableCell>
                      <TableCell>{confChip(row.vat, 'VAT')}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.status} variant="outlined" sx={fitChipSx} />
                      </TableCell>
                      <TableCell align="right" className="fit-table-no-clip">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" onClick={() => setWhyRow(row)}>
                            Why
                          </Button>
                          <Button size="small" variant="contained" onClick={() => acceptRow(row.tx_id)}>
                            Accept
                          </Button>
                          <Button size="small" color="inherit" onClick={() => rejectRow(row.tx_id)}>
                            Reject
                          </Button>
                          {onOpenTx ? (
                            <Button size="small" onClick={() => onOpenTx(row.tx_id)}>
                              Open
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200, 500]}
          />
        </CardContent>
      </Card>

      {whyRow ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Why — TX {whyRow.tx_id}
            </Typography>
            {(['cashflow', 'supplier', 'category', 'vat'] as const).map((dim) => {
              const blob = whyRow[dim] as DimBlob | undefined;
              return (
                <Box key={dim} sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {dim}
                  </Typography>
                  <Typography variant="body2">
                    {blob?.rationale || '—'}{' '}
                    {blob?.sources?.length ? `(sources: ${blob.sources.join(', ')})` : ''}
                  </Typography>
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => acceptRow(whyRow.tx_id, [dim])}>
                    Accept {dim} only
                  </Button>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="h6">Theme knowledge</Typography>
            {activeTheme ? (
              <Chip
                size="small"
                color={themeStatusChipColor(themeStatus)}
                label={themeStatus}
                sx={{ textTransform: 'capitalize', alignSelf: { xs: 'flex-start', sm: 'center' } }}
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Human-edited RAG documents used when scoring. Preview renders markdown; switch to Edit to change
            the source, then Publish after correcting drafts.
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField
                select
                size="small"
                label="Theme document"
                value={activeThemeId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (Number.isFinite(id) && id > 0) {
                    selectTheme(id);
                    setThemeBodyMode('preview');
                  }
                }}
                sx={{ flex: 1, minWidth: 240 }}
                disabled={!themes.length}
              >
                {themes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {t.title}
                      </Typography>
                      <Chip size="small" label={t.status} color={themeStatusChipColor(t.status)} />
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={themeStatus}
                onChange={(e) => setThemeStatus(e.target.value)}
                disabled={activeThemeId == null}
                sx={{ width: { xs: '100%', sm: 160 } }}
              >
                <MenuItem value="draft">draft</MenuItem>
                <MenuItem value="published">published</MenuItem>
                <MenuItem value="archived">archived</MenuItem>
              </TextField>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={themeBodyMode}
                onChange={(_, v: 'preview' | 'edit' | null) => {
                  if (v) setThemeBodyMode(v);
                }}
                disabled={activeThemeId == null}
                aria-label="Theme body view mode"
              >
                <ToggleButton value="preview">Preview</ToggleButton>
                <ToggleButton value="edit">Edit</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {!themes.length ? (
              <Typography variant="body2" color="text.secondary">
                No theme docs yet. Use Generate theme drafts.
              </Typography>
            ) : null}
            {activeTheme?.theme_key ? (
              <Typography variant="caption" color="text.secondary">
                Key: <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>{activeTheme.theme_key}</Box>
              </Typography>
            ) : null}
            {themeBodyMode === 'edit' ? (
              <TextField
                size="small"
                label="Title"
                value={themeTitle}
                onChange={(e) => setThemeTitle(e.target.value)}
                disabled={activeThemeId == null}
                fullWidth
              />
            ) : (
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {themeTitle || 'Untitled theme'}
              </Typography>
            )}
            {themeBodyMode === 'preview' ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                  px: { xs: 1.5, sm: 2.5 },
                  py: { xs: 1.5, sm: 2 },
                  maxHeight: { xs: 420, sm: 560 },
                  overflow: 'auto',
                  ...themeMarkdownSx
                }}
              >
                {themeDraft.trim() ? (
                  <ReactMarkdown
                    components={{
                      a: ({ href, children }) => (
                        <Link href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </Link>
                      )
                    }}
                  >
                    {themeDraft}
                  </ReactMarkdown>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    This theme has no body yet. Switch to Edit to add markdown.
                  </Typography>
                )}
              </Box>
            ) : (
              <TextField
                label="Body (markdown)"
                value={themeDraft}
                onChange={(e) => setThemeDraft(e.target.value)}
                multiline
                minRows={16}
                disabled={activeThemeId == null}
                fullWidth
                helperText="Markdown source — headings, lists, and bold render in Preview."
                InputProps={{
                  sx: {
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 13,
                    alignItems: 'flex-start',
                    lineHeight: 1.5
                  }
                }}
              />
            )}
            {activeTheme?.web_snippets && activeTheme.web_snippets.length > 0 ? (
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Web snippets
                </Typography>
                <Stack spacing={1}>
                  {activeTheme.web_snippets.map((snip, idx) => (
                    <Box
                      key={`${snip.url || snip.title || 'snip'}-${idx}`}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.25,
                        py: 1
                      }}
                    >
                      {snip.url ? (
                        <Link href={snip.url} target="_blank" rel="noopener noreferrer" variant="body2">
                          {snip.title || snip.url}
                        </Link>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {snip.title || 'Snippet'}
                        </Typography>
                      )}
                      {snip.snippet ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                          {snip.snippet}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" onClick={saveTheme} disabled={activeThemeId == null}>
                Save theme
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchWebForTheme}
                disabled={activeThemeId == null || !webEnabled}
              >
                Fetch web
              </Button>
              {themeBodyMode === 'edit' ? (
                <Button size="small" onClick={() => setThemeBodyMode('preview')} disabled={activeThemeId == null}>
                  Back to preview
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
